import React from 'react';
import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import PaymentStatusBadge from '../../components/PaymentStatusBadge';
import SourceStatusBanner from './modernwash/SourceStatusBanner';
import { useModernwashResource } from './modernwash/useModernwashResource';
import { fetchModernwashKiosk } from '../../api/modernwash';
import { colors, layout } from '../../design-system/tokens';
import { pick, formatNumber, formatWon, formatDate, formatTime } from './modernwash/format';

/**
 * 키오스크 상태 — ModernWash 원천의 오늘 결제 요약 + 점검 모드 (read-only).
 * 계약: backend dto KioskData = {
 *   paymentsSummary: { date, count, totalAmount } | null,
 *   maintenance:      { washerId, active } | null   // washerId 지정 조회 시에만 채워짐
 * }.
 */
const StatCard = ({ label, value, caption }) => (
  <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
    <Typography variant="caption" sx={{ color: colors.text.secondary, fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{ mt: 1, fontSize: { xs: 21, sm: 24 }, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    {caption && (
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        {caption}
      </Typography>
    )}
  </Paper>
);

const KioskStatus = () => {
  const { loading, reload, source, data } = useModernwashResource(fetchModernwashKiosk);

  const maintenance = data?.maintenance || null; // { washerId, active } | null
  const payments = data?.paymentsSummary || null; // { date, count, totalAmount } | null
  const count = pick(payments, ['count', 'todayCount', 'paymentCount', 'totalCount']);
  const amount = pick(payments, ['totalAmount', 'todayAmount', 'amount', 'paymentAmount']);
  const date = pick(payments, ['date']);
  const hasContent = Boolean(payments || maintenance);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1 }}
      >
        <PageHeader
          title="키오스크 상태"
          description="현장 키오스크의 오늘 결제 요약과 점검 모드예요. ModernWash 원천을 그대로 읽어 보여줍니다."
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
          {!loading && !source.blocking && source.fetchedAt && (
            <Typography variant="caption" sx={{ color: colors.text.muted, whiteSpace: 'nowrap' }}>
              동기화 {formatTime(source.fetchedAt)}
            </Typography>
          )}
          <Tooltip title="새로고침" arrow>
            <span>
              <IconButton onClick={reload} disabled={loading} aria-label="새로고침">
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {loading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
            gap: layout.cardGap,
          }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <Paper key={i} sx={{ p: 2 }}>
              <Skeleton variant="text" width="50%" height={20} />
              <Skeleton variant="text" width="70%" height={38} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" height={18} />
            </Paper>
          ))}
        </Box>
      ) : (
        <>
          <SourceStatusBanner state={source} onRetry={reload} />
          {!source.blocking &&
            (hasContent ? (
              <Stack spacing={layout.cardGap}>
                <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
                    점검 모드
                  </Typography>
                  {maintenance ? (
                    <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 0.75 }}>
                      {maintenance.active ? (
                        <PaymentStatusBadge label="점검 중" status="warning" />
                      ) : (
                        <PaymentStatusBadge label="정상 운영" status="success" />
                      )}
                      {maintenance.washerId && (
                        <Typography variant="caption" sx={{ color: colors.text.muted }}>
                          세차기 {maintenance.washerId}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: colors.text.muted }}>
                      지정된 세차기 점검 상태가 없어요. (세차기 선택 조회 시 표시됩니다)
                    </Typography>
                  )}
                </Paper>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                    gap: layout.cardGap,
                  }}
                >
                  <StatCard
                    label="오늘 결제 건수"
                    value={`${formatNumber(count)}건`}
                    caption={date ? `${formatDate(date)} 기준` : '오늘 00시부터'}
                  />
                  <StatCard
                    label="오늘 결제 금액"
                    value={formatWon(amount)}
                    caption="1일권 현장 결제 합계"
                  />
                </Box>
              </Stack>
            ) : (
              <DataState loading={false} empty emptyText="키오스크 상태 정보가 아직 없어요." />
            ))}
        </>
      )}
    </Box>
  );
};

export default KioskStatus;
